// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import { getNamespacedId } from './idea-navigation.js';
import { InternalHeading } from '../../InternalHeading.js';
import { RSLPriorKnowledgeLink } from '../common-components/RSLPriorKnowledgeLink.js';
import {
  RSLTNInstructionalSupports,
  RSLInstructionalSupportsChildrenChecker,
} from './RSLTNInstructionalSupports.js';
import type { RSLTNInstructionalSupportsChildren } from './RSLTNInstructionalSupports.js';
import { RSLTNTools } from './RSLTNTools.js';
import { RSLTNTeacherLedExamples } from './RSLTNTeacherLedExamples.js';
import { RSLTNIdeaExploration } from './RSLTNIdeaExploration.js';
import { RSLTNRichTask } from './RSLTNRichTask.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { sharedRSLChildrenElementTypes } from '../common-children-types.js';
import type { SharedRSLChildren } from '../common-children-types.js';

export function RSLTNIntroduction(props: {
  children: ChildrenArray<
    | Element<typeof RSLTNPriorKnowledgeSuggestedReview>
    | Element<typeof RSLTNRichTask>
    | Element<typeof RSLTNTools>
    | Element<typeof RSLTNLessonSupports>,
  >,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNIntroduction);
  if (failedValidation) return null;
  return (
    <div>
      <InternalHeading level={2}>Teacher introduction</InternalHeading>
      {props.children}
    </div>
  );
}
RSLTNIntroduction.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(RSLTNPriorKnowledgeSuggestedReview),
    },
    { min: 0, max: 1, elementType: t.elementType(RSLTNRichTask) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNTools) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNLessonSupports) },
  ),
};

export function RSLTNPriorKnowledgeSuggestedReview(props: {
  children: ChildrenArray<Element<typeof RSLPriorKnowledgeLink>>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNPriorKnowledgeSuggestedReview,
  );

  if (failedValidation) return null;
  return (
    <>
      <InternalHeading level={3}>Suggested review</InternalHeading>
      <p>
        Depending on your students’ level of prior knowledge, consider
        revisiting the following lessons:
      </p>
      {React.Children.map(props.children, (child, idx) => (
        <p style={{ margin: '8px auto' }} key={idx}>
          {child}
        </p>
      ))}
    </>
  );
}
RSLTNPriorKnowledgeSuggestedReview.spec = {
  children: t.childrenArray(t.elementType(RSLPriorKnowledgeLink)),
};

export function RSLTNLessonSupports(props: {
  children: RSLTNInstructionalSupportsChildren,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNLessonSupports);
  if (failedValidation) return null;
  return (
    <div>
      <InternalHeading level={3}>Lesson supports</InternalHeading>
      <p>
        The following supports may be useful for this lesson. More specific
        supports may appear throughout the lesson:
      </p>
      {React.Children.map(props.children, (child) =>
        // $FlowIssue cloneElement signature seems dodgy
        React.cloneElement(child, { _isLessonSupport: true }),
      )}
    </div>
  );
}
RSLTNLessonSupports.spec = {
  children: RSLInstructionalSupportsChildrenChecker,
};

export function RSLTNIdeaSection(props: {
  title: string,
  id: string,
  children: ChildrenArray<
    Element<typeof RSLTNIdeaTheory> | Element<typeof RSLTNTeacherLedExamples>,
  >,
  _numPrecedingTeacherLedExamples: number,
  ...CompilerInjectedProps,
}) {
  const { title, id, children, _numPrecedingTeacherLedExamples } = props;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLTNIdeaSection,
  );

  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;

    if (props.id.match(/^[a-z]+(-[a-z]+)*$/) === null) {
      addErrors([
        {
          message:
            '"id" must only use lowercase letters, with words separated by a single dash. Eg. "segmented-sampling"',
          ...locations.id,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  const mappedChildren = React.Children.map(children, (child) => {
    if (child.type !== RSLTNTeacherLedExamples) return child;
    const _child: React$Element<typeof RSLTNTeacherLedExamples> = (child: any);
    return React.cloneElement(_child, { _numPrecedingTeacherLedExamples });
  });

  return (
    <section id={getNamespacedId(id)}>
      <InternalHeading level={2}>{title}</InternalHeading>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 25,
        }}
      >
        {mappedChildren}
      </div>
    </section>
  );
}
RSLTNIdeaSection.spec = {
  title: t.string,
  id: t.string,
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(RSLTNIdeaTheory),
    },
    {
      min: 0,
      max: Infinity,
      elementType: t.elementType(RSLTNTeacherLedExamples),
    },
  ),
};

export function RSLTNIdeaTheory(props: {
  children: ChildrenArray<
    | SharedRSLChildren
    | Element<typeof RSLTNInstructionalSupports>
    | Element<typeof RSLTNIdeaExploration>,
  >,
  ...CompilerInjectedProps,
}) {
  const { children, _performValidation } = props;
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLTNIdeaTheory,
  );
  const indexedChildrenArray = React.Children.toArray(children).map(
    (child, index) => ({ child, index }),
  );
  const explorations = indexedChildrenArray.filter(
    ({ child }) => child.type.name === 'RSLTNIdeaExploration',
  );
  const instructionalSupports = indexedChildrenArray.filter(
    ({ child }) => child.type.name === 'RSLTNInstructionalSupports',
  );
  // supports do not come first
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;
    const [firstChild] = indexedChildrenArray;
    if (firstChild.child.type.name === 'RSLTNInstructionalSupports') {
      addErrors([
        {
          message: `RSLTNIdeaTheory's first child cannot be RSLTNInstructionalSupports`,
          ...locations.RSLTNIdeaTheory,
        },
      ]);
    }
  });
  // check if there is more than one exploration
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;
    if (explorations.length > 1) {
      addErrors([
        {
          message: 'RSLTNIdeaTheory can only include one RSLTNIdeaExploration',
          ...locations.RSLTNIdeaTheory,
        },
      ]);
    }
  });

  // check proper use of instructional supports
  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;
    if (
      explorations.length === 0 &&
      (instructionalSupports.length > 1 ||
        (instructionalSupports.length === 1 &&
          last(indexedChildrenArray).child.type.name !==
            'RSLTNInstructionalSupports'))
    ) {
      addErrors([
        {
          message:
            'If RSLTNIdeaTheory does not include a RSLTNIdeaExploration then RSLTNInstructionalSupports can only be included once at the end',
          ...locations.RSLTNIdeaTheory,
        },
      ]);
    }
  });

  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;
    if (explorations.length === 1) {
      const [exploration] = explorations;
      if (
        instructionalSupports.some(
          ({ index }) =>
            ![exploration.index - 1, exploration.index + 1].includes(index),
        )
      ) {
        addErrors([
          {
            message:
              'If RSLTNIdeaTheory includes a RSLTNIdeaExploration then RSLTNInstructionalSupports can only be adjacent to it.',
            ...locations.RSLTNIdeaTheory,
          },
        ]);
      }
    }
  });

  if (failedValidation) return null;
  return <div>{props.children}</div>;
}
RSLTNIdeaTheory.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      ...sharedRSLChildrenElementTypes,
      t.elementType(RSLTNInstructionalSupports),
      t.elementType(RSLTNIdeaExploration),
    ),
  ),
};

function last<T>(xs: Array<T>): T {
  return xs[xs.length - 1];
}
